import React, { ReactNode } from "react";
import {
  Column,
  TableInstance,
  useExpanded,
  usePagination,
  UsePaginationInstanceProps,
  UsePaginationState,
  UseSortByInstanceProps,
  useTable,
} from "react-table";

export type TableInstanceWithHooks<T extends object> = TableInstance<T> &
  UsePaginationInstanceProps<T> &
  UseSortByInstanceProps<T> & {
    state: UsePaginationState<T>;
  };

declare type ExtendedColumnGroups<T extends object> = Column<T> & {
  header?: string;
  className?: string;
  headerClassName?: string;
  Header?: any;
  cellClassName?: string;
};

declare type ColSpanHeader = {
  header: string;
  id: string;
  colspan: string;
};

interface CustomTableProps<T extends object> {
  columnList: ExtendedColumnGroups<T>[];
  colSpanHeader?: ColSpanHeader[];
  className?: string;
  selectable?: boolean;
  checkJobId?: boolean;
  thClassName?: string;
  tdClassName?: string;
  serverData: T[];
  pageSize200?: boolean;
  extraLastTableRow?: ReactNode;
  selectedRows: number[];
  onRowClicked: (item: any, index: number) => void;
}

interface ITable<T extends object> {
  columns: ExtendedColumnGroups<T>[];
  colSpanHeader: ColSpanHeader[];
  data: T[];
  checkJobId: boolean;
  pageSize200?: boolean;
  fetchData: ({
    pageIndex,
    pageSize,
  }: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  loading: boolean;
  totalResult: number;
  onRowClicked: (item: any, index: number) => void;
  selectable?: boolean;
  pageCount: number;
  className: string;
  thClassName: string;
  selectedRows: T[];
  extraLastTableRow: ReactNode;
}

const Table = <T extends object>({
  columns,
  pageSize200,
  colSpanHeader,
  data,
  checkJobId,
  fetchData,
  loading,
  totalResult,
  onRowClicked,
  selectable,
  pageCount: controlledPageCount,
  ...props
}: ITable<T>) => {
  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    // Get the state from the instance
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: pageSize200 ? 200 : 5,
      }, // Pass our hoisted table state
      manualPagination: true, // Tell the usePagination
      pageCount: controlledPageCount,
    },
    usePagination
  ) as TableInstanceWithHooks<any>;

  // Listen for changes in pagination and use the state to fetch our new data
  React.useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  // Render the UI for your table
  return (
    <div>
      <div className="table-responsive">
        <table
          className={`table base-table m-0 table-bordered ${
            selectable ? "selectable" : ""
          } ${props.className}`}
          {...getTableProps([
            {
              style: {
                width: "100%",
              },
            },
          ])}
        >
          <thead>
            {colSpanHeader && (
              <tr>
                {colSpanHeader.map((e: any, index) => {
                  return (
                    <th
                      className={e?.className}
                      key={e?.id}
                      colSpan={e?.colspan}
                    >
                      {e.header}
                    </th>
                  );
                })}
              </tr>
            )}
            <tr>
              {columns.map((e: any, index) => {
                return (
                  <th className={props.thClassName} key={index}>
                    {e.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              0;
              return (
                <tr
                  {...row.getRowProps((state, rowInfo) => {
                    if (rowInfo && rowInfo.row) {
                      return {
                        ...row.getRowProps(),
                        onKeyPress: (e) => {
                          if (!selectable) return;
                          if (e.key == " " || e.key == "Enter") {
                            e.preventDefault();
                            e.currentTarget.click();
                          }
                        },
                        onClick: (e) => {
                          if (
                            typeof onRowClicked !== "undefined" &&
                            selectable
                          ) {
                            onRowClicked(row.original, rowInfo.row.index);
                          }
                        },
                        className: props.selectedRows.includes(
                          checkJobId ? row.original.job?.id : row.original.id
                        )
                          ? "active"
                          : "",
                        tabIndex: selectable ? 0 : -1,
                      };
                    } else {
                      return row.getRowProps();
                    }
                  })}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps([
                          {
                            className: (cell.column as any).className,
                          },
                        ])}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {props.extraLastTableRow}
          </tbody>
        </table>
      </div>
      {/* 
        Pagination can be built however you'd like. 
        This is just a very basic UI implementation:
      */}
      <div className="pagination bg-light-grey border-dark border justify-content-between">
        <div className="d-flex flex-column flex-sm-row ps-1">
          {!pageSize200 && (
            <select
              value={pageSize}
              className="bg-light-grey border-0"
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          )}

          {loading ? (
            // Use our custom loading state to show a loading indicator
            <span className="align-items-center d-flex flex-fill ps-2">
              Loading...
            </span>
          ) : (
            <span className="align-items-center d-flex flex-fill ps-2">
              Showing {page.length} of ~{totalResult} results
            </span>
          )}
        </div>

        <div className="d-flex flex-column flex-sm-row">
          <span className="align-items-center d-flex px-3">
            Page {pageIndex + 1} of {pageOptions.length}
          </span>

          <button
            title="prev page"
            aria-label="Previous Page"
            className="py-2 px-3 btn flat-btn"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            {"<"}
          </button>
          <button
            title="next page"
            aria-label="Next Page"
            className="py-2 px-3 btn flat-btn"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            {">"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomTable = <T extends object>(props: CustomTableProps<T>) => {
  // We'll start our table without any data
  const [data, setData] = React.useState<T[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [pageCount, setPageCount] = React.useState(0);
  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(
    ({ pageSize, pageIndex }) => {
      // This will get called when the table needs new data
      // You could fetch your data from literally anywhere,
      // even a server. But for this example, we'll just fake it.

      // Give this fetch an ID
      const fetchId = ++fetchIdRef.current;

      // Set the loading state
      setLoading(true);
      // We'll even set a delay to simulate a server here

      setTimeout(() => {
        // Only update the data if this is the latest fetch
        if (fetchId === fetchIdRef.current) {
          const startRow = pageSize * pageIndex;
          const endRow = startRow + pageSize;
          setData(props.serverData.slice(startRow, endRow));

          // Your server could send back total page count.
          // For now we'll just fake it, too
          setPageCount(Math.ceil(props.serverData.length / pageSize));

          setLoading(false);
        }
      }, 150);
    },
    [props.serverData]
  );

  return (
    <div>
      <div className="customTable">
        <Table
          columns={props.columnList}
          data={data}
          pageSize200={props.pageSize200}
          totalResult={props.serverData.length}
          colSpanHeader={props.colSpanHeader}
          fetchData={fetchData}
          loading={loading}
          pageCount={pageCount}
          checkJobId={props.checkJobId}
          {...(props as any)}
        />
      </div>
    </div>
  );
};

export default CustomTable;
